import { coreHotelCfarOffersThemeVars } from "@b2bportal/core-hotels-cfar-offers";
import { coreDefaultUiColors } from "@b2bportal/core-ui";

/** RBC Style Guide Colors **/
const tenantColourPalette = {
  "rbc-bright-blue": "#0051A5",
  "rbc-bright-blue-light": "#186CE6",
  tundra: "#97C6D5",
  turquoise: "#2AD2C9",
  "turquoise-30": "#BFF1EF",
  "rbc-gray-blue": "#E7EEF1",
  "rbc-light-blue": "#CBE3EA",
  sky: "#09BCEF",
  "sky-30": "#B5EBFA",
  "sky-tint-2": "#D3ECF7",
  "turquoise-tint-2": "#D0F4F2",
  "navy-blue": "#0B3166",
  "cool-white": "#E7EEF1",
  sunburst: "#FFB500",
  "sunburst-30": "#FFE9B2",
  "sunburst-60": "#FFD366",
  sunset: "#FF6C54",
  "sunset-30": "#FFC4BB",
  blossom: "#FF79F2",
  orchid: "#CF46FF",
  "orchid-30": "#F5DAFF",
  forest: "#1D9181",
  leaf: "#9BE73B",
  "leaf-30": "#EBFAD8",
  "leaf-60": "#C3F189",
  lime: "#D6EB21",
  "lime-60": "#EAF590",
  "dark-blue": "#002750",
  "neutral-grey": "#585858",
  "grey-light-tint-3": "#EAEAEA",
  "warm-red-tint-3": "#FEEBE9",
};

/** RBC Variables mentioned in Figma */
const tenantFigmaVariables = {
  /** RBC Typography variables */
  "text-default": "#1F1F1F",
  "text-low-emphasis": "#6F6F6F",
  "text-tertiary": "#565656",
  "text-disabled": "#919191",
  "text-inverted": "var(--white)",

  "interaction-link": "#0051A5",
  "interactive-1": tenantColourPalette["rbc-bright-blue"],
  "interactive-1-light":
    "color-mix(in srgb, var(--interactive-1) 10%, transparent)",
  "interactive-hover-1": tenantColourPalette["navy-blue"],
  "interactive-1-active": "#071D3D",
  "interactive-1-text": "#EDEDED",
  "background-1": "var(--white)",
  "background-2": "#F5F8F9",
  "background-3": "var(--cool-white)",
  "banner-info-background": "var(--cool-white)",
  "background-disabled": "#EAEAEA",
  "border-1": "#B3B3B3",
  "border-2": "#1F1F1F",
  "border-3": "#E0E0E0",
  "green-20": "#31ad6f0d",
  "border-4": "var(--rbc-gray-blue)",
  "base-page-background": "var(--background-2)",
  "light-background":
    "linear-gradient(139deg, #a7cfdb 0%, var(--cool-white) 61.98%)",
  // Grid V2 "Ways to pay" card headers: the refreshed Fixed pricing card, and
  // the Flex / Credit card ones.
  "grid-v2-refreshed-header-background":
    "linear-gradient(12.2deg, var(--rbc-bright-blue) 32%, var(--rbc-bright-blue-light) 95%)",
  "grid-v2-card-header-background":
    "linear-gradient(90deg, var(--turquoise-tint-2) 0%, var(--sky-tint-2) 100%)",
  "action-error": "#B91A0E",
  information: "#276AE6",
  "action-information": "#456B86",
  "action-warning": "#FFBA00",
  "action-success": "#097B24",
  "action-success-dark": "#06641d",
  "action-success-darker": "#065218",
  "selected-1": tenantColourPalette["rbc-bright-blue"],
  "accent-7": "#FFE9B2",
  "accent-8": tenantColourPalette.sunburst,
  "header-background": "#f8f8f8",
  "action-background-information": "#F3F7F8",
  "action-background-success": "#EBFAEF",
  "action-background-warning": "#FFF6E7",
  "action-background-error": "#FEEBE9",
};

const tenantColors = {
  ...tenantColourPalette,
  ...tenantFigmaVariables,
};

const coreColors = {
  /** core Color overrides **/
  primary: tenantColors["rbc-bright-blue"],
  secondary: tenantColors["navy-blue"],
  "text-primary": tenantColors["text-default"],
  "text-secondary": tenantColors["text-low-emphasis"],
  border: tenantColors["border-1"],
  error: tenantColors["action-error"],
};

const tenantCarColors = {
  "car-rental-pill": "rgba(0, 129, 64, 1.0)",
};

const tenantCfarColors = {
  "synthetic-fare-filter-banner-background": "var(--sky-30)",
  "core-cfar-flights-icon-background-color": "#FFD366",
  "core-cfar-flights-exercise-header-font-family": "var(--font-family-rbc)",
  "core-cfar-synthetic-modal-bullet-color": "var(--navy-blue)",
};

const tenantFlightColors = {
  "success-tick-color": "#00847F",
  "segment-icon-color": "#CECFCD",
  // PRICE PREDICTION
  "price-prediction-wait": "#FF6A6A",
  "price-prediction-fair": tenantFigmaVariables["accent-8"],
  "price-prediction-good": tenantFigmaVariables["accent-7"],
  "price-prediction-great": tenantColourPalette.leaf,

  // PRICE PREDICTION GRADIENT COLORS
  "price-prediction-gradient-wait": "#FF6A6A",
  "price-prediction-gradient-fair": tenantFigmaVariables["accent-8"],
  "price-prediction-gradient-good": tenantFigmaVariables["accent-7"],
  "price-prediction-gradient-great": tenantColourPalette.leaf,

  // Fare Class colors
  "fare-class-basic": "rgba(115, 176, 227, 0.1)",
  "fare-class-standard": "rgba(0, 106, 195, 0.25)",
  "fare-class-enhanced": "rgba(0, 81, 165, 0.5)",
  "fare-class-premium": "rgba(0, 49, 104, 0.75)",
  "fare-class-luxury": tenantColourPalette["dark-blue"],

  // Flight fare list and cards
  "fare-list-background": "var(--white)",
  "flight-card-fare-list-item-background": "var(--background-2)",
  "flight-card-fare-list-item-border": "0.5px solid #CECFCD",
  "flight-card-fare-list-item-header-background": "var(--sky-30)",
};

const tenantHotelColors = {
  // Map markers
  "map-marker-border": "1px solid var(--navy-blue)",
  "map-marker-shadow": "0 6px 12px -3px rgba(0, 0, 0, 0.40)",
  "hotel-shop-section-border": "var(--card-border)",
  // Hotel Section shadows
  "hotel-section-shadow": "0px 3px 8px 0px #0000001a",
  // Hotel skeleton colors
  "tenant-color__skeleton-background": "#e0e0e0",
  "tenant-color__skeleton-loader": tenantColors["cool-white"],
};
const fintechColors = {
  "hts-provided-by-text-color": "#000000",
  "core-fintech-ui-exercise-success-header-font-family":
    "var(--font-family-rbc)",
};

const fintechDisruptionColors = {
  "bullet-point-icon-background-color": "#E7EAF0",
  "bullet-point-icon-background-3-color": "#f3f4f5",
  "bullet-point-icon-color": "#133C8F",
  "disruption-exercise-action-link-color": "#006AC3",
};
export const coreUiColors = {
  ...coreDefaultUiColors,
  "core-color-input-label": "#1F1F1F",
  "core-color-input-error": tenantColors["action-error"],
  "core-color-info-bg-fill": "#0051A5",

  //Map
  "core-color__map-marker-background": "#fff",
  "core-color__map-marker-text": tenantColors["rbc-bright-blue"],
  "core-color__map-marker-hover-text": "#ffffff",
  "core-color__map-marker-hover-background": tenantColors["navy-blue"],

  // Generic
  "dialog-z-index": 10,
};

export const themeVariables = {
  "primary-text": "var(--text-primary)",
  "background-color": tenantColors["cool-white"],
  "background-highlight": tenantColors["rbc-light-blue"],
  "popup-background-color": "#fff",
  "tooltip-shadow": "0 4px 5px rgba(0, 0, 0, 0.2)",
  "background-gradient-start": tenantColors["rbc-bright-blue"],
  "background-gradient-end": tenantColors["rbc-bright-blue"],
  "page-background-color": tenantColors["base-page-background"],
  white: "#fff",
  "border-radius-md": "8px",

  "disabled-background-color": "#EAEAEA",

  "homepage-title-primary": tenantColors["navy-blue"],

  "font-family": ["Roboto", "Proxima Nova", "sans-serif"].join(","),

  // RBC's custom font
  "font-family-rbc": [
    "RBC Display",
    "Roboto",
    "Proxima Nova",
    "sans-serif",
  ].join(","),

  ////////

  /** Common */
  "link-primary": "rgba(1, 61, 88, 1.0)",

  /** Button */
  "button-hover-background-color": tenantColors["navy-blue"],
  "button-active-background-color": "#071D3D",
  "button-disabled-background-color": "#D9D9D9",
  "button-primary": "#fff",
  "button-primary-background": tenantColors["rbc-bright-blue"],
  "button-primary-hover": "#fff",
  "button-primary-disabled": "var(--text-disabled)",
  "button-primary-disabled-background":
    "var(--button-disabled-background-color)",
  "button-secondary": "var(--interaction-link)",
  "button-secondary-background": "var(--white)",
  "button-secondary-hover-background": "var(--background-highlight)",
  "button-secondary-border": "var(--interaction-link)",
  "button-secondary-disabled": "var(--text-disabled)",
  "button-secondary-disabled-background":
    "var(--button-disabled-background-color)",
  "button-tertiary": tenantColors["rbc-bright-blue"],
  "button-tertiary-background": "transparent",
  "button-tertiary-border": "transparent",
  "button-tertiary-hover": "var(--button-hover-background-color)",
  "button-tertiary-hover-background": "var(--background-highlight)",
  "button-tertiary-hover-border": "transparent",
  "button-tertiary-disabled": "var(--text-disabled)",
  "button-tertiary-disabled-border": "transparent",
  "button-tertiary-disabled-background":
    "var(--button-disabled-background-color)",

  /** Loading */
  "loading-icon-track-color": "transparent",
  "loading-icon-border-color": "rgba(2, 118, 177, 1.0)",
  "loading-background-color": "var(--primary)",
  "loading-text-color": "#fff",

  /** ========= Radio Button ======== */
  "radio-button-border-color": tenantColors["border-1"],
  "radio-button-selected-color": tenantColors["selected-1"],

  /** ========= UI ======== */
  "card-features": "var(--action-success)",
  "card-border": "2px solid var(--cool-white)",
  "card-border-thin": "1px solid var(--cool-white)",
  "divider-border": "1px solid var(--border-3)",
  "divider-border-light": "1px solid var(--neutral-fill-color)",
  "slider-background-color": "rgba(216, 216, 216, 1.0)",
  "slider-range-background-color": "var(--primary)",
  "slider-thumb-background-color": "var(--primary)",
  "slider-thumb-value-background-color": "var(--primary)",
  "slider-thumb-value-color": "#fff",

  "page-header-background-color": "#fff",
  "page-header-text-color": "var(--text-default)",
  "page-footer-background-color": "#fff",
  "page-footer-text-color": "var(--text-default)",

  "checkbox-border-color": "var(--border)",
  "checkbox-checked-border-color": "var(--border-1)",
  "checkbox-checked-background-color": "var(--primary)",
  "checkbox-checked-icon-color": "#fff",
  "checkbox-checked-disabled-background-color": "#676868",
  "checkbox-disabled-background-color": "#878787",
  "checkbox-disabled-border-color": "var(--border-3)",
  "switch-checked-color": "var(--primary)",

  "input-background-color": "#fff",
  "input-text-disabled": "#6F6F6F",
  "input-border-color": "var(--border)",
  "input-error-color": "var(--error)",
  "input-error-border-color": "var(--error)",
  "input-disabled-background-color": "var(--disabled-background-color)",
  "input-disabled-label-color": "#919191",
  "input-disabled-text-color": "var(--input-text-disabled)",
  "input-focus-color": "var(--primary)",
  "input-hover-color": "var(--secondary)",

  "notification-banner-background-color": "var(--secondary)",
  "notification-banner-text-color": "#fff",

  "location-autocomplete-icon-background-color": "#F6F6F6",
  "location-autocomplete-icon-color": "var(--text-low-emphasis)",

  "map-marker-text": "var(--text-default)",
  "map-marker-surface": "var(--background-color)",
  "map-marker-hover-text": "#ffffff",
  "map-marker-hover-surface": "var(--primary)",

  "filter-popup-active-background": "rgba(225, 245, 255, 1)",
  "filter-popup-active-border": "rgba(1,61,88,.1)",
  "star-rating-color": "var(--sunburst)",

  // CALENDAR COLORS
  "calendar-labels-background-color": "#DDD",
  "date-selected-text-color": "#FFFFFF",
  "date-hover-text-color": "#FFFFFF",
  "bucket-0-text-color": tenantColors["text-default"],
  "bucket-1-text-color": tenantColors["text-default"],
  "bucket-2-text-color": tenantColors["text-default"],
  "bucket-3-text-color": tenantColors["text-default"],
  "bucket-0-color": tenantColourPalette["leaf-60"],
  "bucket-1-color": tenantColourPalette["sunburst-30"],
  "bucket-2-color": tenantColourPalette["sunset-30"],
  "bucket-3-color": "#FF6C54CC",
  "white-font-color": "rgba(255, 255, 255, 1.0)",
  "grey-font-color": "rgba(80, 80, 80, 1.0)",
  "date-hover-color": tenantColors["interactive-hover-1"],
  "date-selected-color": tenantColors["interactive-1"],
  "date-selected-range-color": tenantColors["sky-30"],
  "date-range-limit-color": tenantColors["interactive-1"],
  "date-disabled-color": "rgba(216, 216, 216, 1.0)",
  "date-selected-background-color": "#fff",
  "carousel-arrow-background": "rgba(51, 51, 51, 0.9)",
  "algomerch-tag-background": tenantColourPalette["leaf-30"],
  "algomerch-tag-text": tenantColors["text-default"],
  "algomerch-tag-border": "1px solid var(--leaf)",

  // CALENDAR A11Y COLORS
  "calendar-a11y-bucket-0-bg": "#e1ebef",
  "calendar-a11y-bucket-0-text": tenantColors["text-default"],
  "calendar-a11y-bucket-1-bg": "#87afbf",
  "calendar-a11y-bucket-1-text": tenantColors["text-default"],
  "calendar-a11y-bucket-2-bg": "#335266",
  "calendar-a11y-bucket-2-text": tenantColors["text-inverted"],
  "calendar-a11y-bucket-3-bg": "#0b1014",
  "calendar-a11y-bucket-3-text": tenantColors["text-inverted"],
  "calendar-a11y-selected-bg": tenantColors["background-1"],
  "calendar-a11y-selected-border": "#000000",
  "calendar-a11y-selected-text": "#000000",

  // TRIPS
  "trips-breadcrumbs-color-text": "#fff",
  "trips-heading-color-text": "#fff",
  "reservation-status-label-success": "var(--leaf-30)",
  "reservation-status-label-success-border": "var(--leaf)",
  "reservation-status-label-neutral": "var(--grey-light-tint-3)",
  "reservation-status-label-neutral-border": "var(--neutral-grey)",
  "reservation-status-label-caution": "#FFFCE5",
  "reservation-status-label-caution-border": "#FBAA26",
  "reservation-status-label-error": "var(--warm-red-tint-3)",
  "reservation-status-label-error-border": "var(--action-error)",

  "background-secondary": tenantColourPalette["cool-white"],

  // Surface
  "surface-background": "#dcdcdc",
  "success-fill-color": "#0F9842",
  "error-fill-color": "#FD2C2C",
  "caution-fill-color": "#CA8A04",
  "neutral-fill-color": "#E3E3E3",
  "text-inverted-color": "#fff",

  // Support chat
  "chat-fab-icon-color": "var(--primary)",
  "chat-bg-agent": "var(--background-color)",
  "chat-bg-customer": "#303030",
  "chat-bg-customer-error": "#EAEAEA",
  "chat-bg-fab": "var(--background-color)",
  "chat-bg-fab-hover": "var(--background-color)",
  "chat-control-border-color": "#c9c9c9",
  "chat-message-border-color": "#f0f0f0",
  "chat-text-agent": "rgba(0, 0, 0, 0.92)",
  "chat-text-customer": "var(--background-color)",
  "chat-text-customer-error": "var(--secondary-text)",
  "chat-text-deeplink": "var(--text-primary)",
  "z-modal": 1300,

  "homescreen-tag-background": "#f4b840",

  ...coreColors,
  ...coreUiColors,
  ...tenantColors,
  ...tenantCarColors,
  ...tenantFlightColors,
  ...tenantHotelColors,
  ...fintechColors,
  ...coreHotelCfarOffersThemeVars(),
  ...fintechDisruptionColors,
  ...tenantCfarColors,
};
